import { auth } from '@/core/auth/authService';
import { CookieNames, cookies } from '@/core/cookies';
import { env } from '@/core/env';

/** Shape of Angular's HttpErrorResponse, so `errorMessage(error)` and `error.error` keep working everywhere. */
export interface ApiError {
  name: 'HttpErrorResponse';
  ok: false;
  status: number;
  statusText: string;
  url: string | null;
  message: string;
  headers: Headers;
  /** Parsed JSON, raw text, a Blob (for blob requests) or a ProgressEvent (network failure). */
  error: unknown;
}

export type QueryParams = Record<string, string | number | boolean | null | undefined>;

export interface ApiRequestOptions {
  /** Plain objects/arrays are JSON-encoded; FormData, Blob, ArrayBuffer and strings are sent as they are. */
  body?: unknown;
  headers?: Record<string, string>;
  params?: QueryParams;
  responseType?: 'json' | 'blob' | 'text';
}

export interface ApiResponse<T> {
  body: T;
  status: number;
  headers: Headers;
}

/** Angular HttpClient default Accept header. */
const ACCEPT = 'application/json, text/plain, */*';

/** Is this URL one of our own API endpoints? Only those get the bearer token and user headers. */
export function isApiRequest(url: string): boolean {
  return (
    url.startsWith(env.apiVersionUrl) || url.startsWith('/api/') || url.startsWith(`${window.location.origin}/api/`)
  );
}

const STANDARD_ENCODING_REPLACEMENTS: Record<string, string> = {
  '40': '@',
  '3A': ':',
  '24': '$',
  '2C': ',',
  '3B': ';',
  '3D': '=',
  '3F': '?',
  '2F': '/',
};

/** Angular HttpParams' encoding: encodeURIComponent, then these characters are left readable (a space stays %20). */
function standardEncoding(value: string): string {
  return encodeURIComponent(value).replace(
    /%(\d[a-f0-9])/gi,
    (match, code: string) => STANDARD_ENCODING_REPLACEMENTS[code] ?? match,
  );
}

/** Relative paths are prefixed with the API base, exactly like `${environment.apiVersionUrl}Client/...`. */
export function resolveUrl(path: string, params?: QueryParams): string {
  const base = /^(https?:)?\/\//.test(path) || path.startsWith('/') ? path : `${env.apiVersionUrl}${path}`;
  const pairs: string[] = [];
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value !== null && value !== undefined) {
      pairs.push(`${standardEncoding(key)}=${standardEncoding(String(value))}`);
    }
  }
  const qs = pairs.join('&');
  return qs ? `${base}${base.includes('?') ? '&' : '?'}${qs}` : base;
}

/** HttpClient's content-type detection for a request body. */
function detectContentType(body: unknown): string | null {
  if (body === null || body === undefined) return null;
  if (body instanceof FormData || body instanceof ArrayBuffer) return null;
  if (body instanceof Blob) return body.type || null;
  if (typeof body === 'string') return 'text/plain';
  return 'application/json';
}

function toFetchBody(body: unknown): BodyInit | null {
  if (body === null || body === undefined) return null;
  if (typeof body === 'string' || body instanceof FormData || body instanceof Blob || body instanceof ArrayBuffer) {
    return body as BodyInit;
  }
  return JSON.stringify(body);
}

/** The headers the API sends back are copied into cookies on every successful response. */
function applyUserDetails(headers: Headers): void {
  const userId = headers.get('userid');
  const businessId = headers.get('businessid');
  const themeColor = headers.get('themecolor');

  cookies.set(CookieNames.isLoggedIn, userId ? 'true' : 'false');
  if (userId) cookies.set(CookieNames.userId, userId);
  if (businessId) cookies.set(CookieNames.businessId, businessId);
  if (themeColor) cookies.set(CookieNames.themeColor, themeColor);
}

function buildError(response: Response | null, url: string, error: unknown): ApiError {
  const status = response?.status ?? 0;
  const statusText = response ? response.statusText : 'Unknown Error';
  return {
    name: 'HttpErrorResponse',
    ok: false,
    status,
    statusText,
    url,
    message: `Http failure response for ${url}: ${status} ${statusText}`,
    headers: response?.headers ?? new Headers(),
    error,
  };
}

/** Reads an error body the way HttpClient does: JSON when it parses, otherwise the raw text. */
async function readErrorBody(response: Response, responseType: 'json' | 'blob' | 'text'): Promise<unknown> {
  if (responseType === 'blob') {
    return response.blob();
  }
  const text = await response.text();
  if (responseType === 'json' && text) {
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  }
  return text;
}

/**
 * The single HTTP entry point (Angular HttpClient + CustomHttpInterceptor + LoaderInterceptor):
 *  - API requests get `Authorization: Bearer <access token>`, `userid` and `businessid` (from cookies);
 *  - successful API responses copy the `userid`/`businessid`/`themecolor` headers into cookies;
 *  - a 401 signs the user out and goes to /login; the error is still rethrown; no retry, no toast.
 * (LoaderInterceptor only ever hid a spinner that was never rendered, so there is nothing to port.)
 */
export async function apiRequest<T = unknown>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  path: string,
  options: ApiRequestOptions = {},
): Promise<ApiResponse<T>> {
  const url = resolveUrl(path, options.params);
  const responseType = options.responseType ?? 'json';
  const api = isApiRequest(url);

  const headers = new Headers({ Accept: ACCEPT });
  const contentType = detectContentType(options.body);
  if (contentType) headers.set('Content-Type', contentType);
  for (const [name, value] of Object.entries(options.headers ?? {})) headers.set(name, value);

  if (api) {
    const token = await auth.getAccessToken();
    headers.set('userid', cookies.get(CookieNames.userId));
    headers.set('businessid', cookies.get(CookieNames.businessId));
    if (token) headers.set('Authorization', `Bearer ${token}`);
  }

  let response: Response;
  try {
    response = await fetch(url, { method, headers, body: toFetchBody(options.body) });
  } catch {
    throw buildError(null, url, new ProgressEvent('error'));
  }

  if (!response.ok) {
    if (api && response.status === 401) {
      auth.signOut();
    }
    throw buildError(response, url, await readErrorBody(response, responseType));
  }

  if (api) {
    applyUserDetails(response.headers);
  }

  let body: unknown;
  if (responseType === 'blob') {
    body = await response.blob();
  } else {
    const text = await response.text();
    if (responseType === 'text') {
      body = text;
    } else if (!text) {
      body = null;
    } else {
      try {
        body = JSON.parse(text);
      } catch (parseError) {
        throw { ...buildError(response, url, { error: parseError, text }), ok: false };
      }
    }
  }

  return { body: body as T, status: response.status, headers: response.headers };
}

const bodyOf = async <T>(promise: Promise<ApiResponse<T>>): Promise<T> => (await promise).body;

/** Convenience wrappers returning only the body (Angular `observe: 'body'`). */
export const api = {
  get: <T = unknown>(path: string, options?: Omit<ApiRequestOptions, 'body'>) =>
    bodyOf(apiRequest<T>('GET', path, options)),
  post: <T = unknown>(path: string, body?: unknown, options?: Omit<ApiRequestOptions, 'body'>) =>
    bodyOf(apiRequest<T>('POST', path, { ...options, body })),
  put: <T = unknown>(path: string, body?: unknown, options?: Omit<ApiRequestOptions, 'body'>) =>
    bodyOf(apiRequest<T>('PUT', path, { ...options, body })),
  delete: <T = unknown>(path: string, options?: Omit<ApiRequestOptions, 'body'>) =>
    bodyOf(apiRequest<T>('DELETE', path, options)),
  /** Full response (Angular `observe: 'response'`) for blob downloads that need `Content-Disposition`. */
  request: apiRequest,
};
